import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { QuoteQuestionAnswer } from '../../../../../../types/QuoteQuestion';
import {
  patchStorage,
  retrieveStorageById,
} from '../../../../../common/src/legacy/services/storeService';
import { RootState } from '../reducers/rootReducer';

export interface AnswersObject {
  [x: string]: QuoteQuestionAnswer[];
}
interface ProFlowStorageController {
  answers: AnswersObject;
  currentlyEditing: string[];
}

interface SetAnswersAction {
  answers: QuoteQuestionAnswer[];
  sessionId: string;
}

interface AnswerAction {
  answer: QuoteQuestionAnswer;
  sessionId: string;
}

export const persistProFlowStorage = createAsyncThunk(
  'questions/persistQuestionsState',
  (sessionId: string, { getState }) => {
    const state = (getState() as RootState)?.proFlowStorage;
    if (state) {
      return patchStorage('pro_flow', sessionId, {
        answers: flattenAnswers(state?.answers),
      });
    }
    return Promise.reject();
  },
);

export const loadProFlowStorage = createAsyncThunk(
  'questions/loadQuestionsState',
  async (sessionId: string, { dispatch }) => {
    const { answers } = await retrieveStorageById('pro_flow', sessionId);
    dispatch(setAnswers({ answers, sessionId }));
  },
);

export const getRiskObjectsIds = async (id: string): Promise<string[]> => {
  const { risk_object } = (await retrieveStorageById('pro_flow', id)) || {};
  return risk_object?.ids || [];
};

export const flattenAnswers = (answers: AnswersObject): QuoteQuestionAnswer[] =>
  Object.values(answers).flat();

const proFlowStorageController = createSlice({
  name: 'proFlowStorage',
  initialState: {
    answers: {},
    currentlyEditing: [],
  } as ProFlowStorageController,
  reducers: {
    addAnswer(state, action: PayloadAction<AnswerAction>) {
      const { answer } = action.payload;
      const answerTag = answer.tag?.toUpperCase() || '';
      const index = state.answers?.[answerTag]?.findIndex(
        ({ id: _id }) => _id === answer.id,
      );

      if (index == null || index === -1) {
        if (state.answers?.[answerTag]) state.answers[answerTag]?.push(answer);
        else state.answers[answerTag] = [answer];
      } else if (
        answer.id !== undefined &&
        state.answers[answerTag][index].answer !== answer.answer
      ) {
        state.answers[answerTag][index] = answer;
      }
    },
    removeAnswer(state, action: PayloadAction<string>) {
      const answer = flattenAnswers(state.answers)?.find(
        ({ id: _id }) => _id === action.payload,
      );

      if (answer) {
        const answerTag = answer.tag?.toUpperCase() || '';
        const index = state.answers?.[answerTag]?.findIndex(
          ({ id: _id }) => _id === answer.id,
        );
        state.answers?.[answerTag]?.splice(index, 1);
      }
    },
    setAnswers(state, action: PayloadAction<SetAnswersAction>) {
      const { answers } = action.payload;

      if (answers) {
        // IMPORTANT: Make sure to RESET answers before filling them
        state.answers = {};

        if (answers.length > 0) {
          answers.forEach((answer) => {
            const answerTag = answer.tag?.toUpperCase() || '';
            state.answers[answerTag] = [
              ...(state.answers[answerTag] || []),
              answer,
            ];
          });
        }
      }
    },
    resetAnswersForRiskObjectType(state, action: PayloadAction<string>) {
      state.answers[action.payload?.toUpperCase()] = [];
    },
    setCurrentlyEditing(state, action: PayloadAction<string[]>) {
      state.currentlyEditing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(persistProFlowStorage.rejected, () => {
      console.error('Failed to persist data');
    });
    builder.addCase(loadProFlowStorage.rejected, (state, action) => {
      console.error('Failed to load data');
    });
  },
});

export const {
  addAnswer,
  removeAnswer,
  setAnswers,
  resetAnswersForRiskObjectType,
  setCurrentlyEditing,
} = proFlowStorageController.actions;

export default proFlowStorageController.reducer;
